import { signWithSigningKey, verifyEd25519, type SigningKey } from './ed25519Provider';
import type { PublicKey } from '@shared/types';
import { base64ToBytes, bytesToBase64, utf8ToBytes } from '../utils/encoding';

function canonicalize(value: unknown): string {
  if (value === null || typeof value !== 'object') {
    return JSON.stringify(value);
  }

  if (Array.isArray(value)) {
    return `[${value.map((item) => canonicalize(item)).join(',')}]`;
  }

  const keys = Object.keys(value as Record<string, unknown>).sort();
  const entries = keys.map((key) => {
    const v = (value as Record<string, unknown>)[key];
    return `${JSON.stringify(key)}:${canonicalize(v)}`;
  });
  return `{${entries.join(',')}}`;
}

export async function signPlaintextWithKey(plaintext: string, signingKey: SigningKey): Promise<string> {
  const messageBytes = utf8ToBytes(plaintext);
  const signatureBytes = await signWithSigningKey(messageBytes, signingKey);
  return bytesToBase64(signatureBytes);
}

export async function signPayloadWithKey(payload: unknown, signingKey: SigningKey): Promise<string> {
  const canonical = canonicalize(payload);
  return signPlaintextWithKey(canonical, signingKey);
}

export async function verifyPlaintext(
  plaintext: string,
  signatureBase64: string,
  identityPublicKey: PublicKey
): Promise<boolean> {
  try {
    if (!signatureBase64 || !identityPublicKey?.value) return false;
    const messageBytes = utf8ToBytes(plaintext);
    const signatureBytes = base64ToBytes(signatureBase64);
    const publicKeyBytes = base64ToBytes(identityPublicKey.value);
    return verifyEd25519(messageBytes, signatureBytes, publicKeyBytes);
  } catch {
    return false;
  }
}
